import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import MeetingVideoModal, { parseSummary } from '../components/MeetingVideoModal';
import Icon from '../components/Icon';
import { useSubTab, useSubnavCounts } from '../components/Layout';
import MeetingCalendar, { joinState } from '../components/MeetingCalendar';
import { joinUrl } from '../utils/meet';

const TAB_INFO = {
  calendar: { title: 'Toplantı takvimi', empty: '' },
  upcoming: { title: 'Yaklaşan toplantılar', empty: 'Planlanmış yaklaşan toplantı yok' },
  recorded: { title: 'Toplantı kayıtları', empty: 'Henüz Fireflies kaydı yok' },
  other: { title: 'Diğer geçmiş toplantılar', empty: 'Kaydı olmayan geçmiş toplantı yok' },
};

export default function Meetings() {
  const [meetings, setMeetings] = useState([]);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const tab = useSubTab('/meetings'); // üst menüde Toplantılar'ın altındaki sekme
  const [player, setPlayer] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const { user, can } = useAuth();
  const EMPTY_FORM = { title: '', lead_id: '', account_id: '', meeting_date: '', duration_minutes: '30', attendees: '', notes: '', google_calendar: true, emails: [] };
  const [form, setForm] = useState(EMPTY_FORM);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  // Takvim: varsayılan "Benim toplantılarım" (davetli / düzenleyen / ekleyen); "Tümü" görebildiği her şey
  const [scope, setScope] = useState(() => { try { return localStorage.getItem('meetingsScope') || 'mine'; } catch (e) { return 'mine'; } });
  const changeScope = (v) => { setScope(v); try { localStorage.setItem('meetingsScope', v); } catch (e) { /* yok */ } };

  useEffect(() => {
    fetchMeetings();
    fetchUsers();
    api.get('/accounts').then(r => setAccounts((r.data || []).filter(a => a.status !== 'churned')
      .sort((a, b) => (a.brand_name || a.company_name).localeCompare(b.brand_name || b.company_name, 'tr')))).catch(() => {});
  }, []);

  // Marka seçilince o markanın kontakları davetli önerisi olarak gelir
  useEffect(() => {
    if (!form.account_id) { setContacts([]); return; }
    api.get(`/accounts/${form.account_id}/contacts`).then(r => setContacts((r.data || []).filter(c => c.email))).catch(() => setContacts([]));
  }, [form.account_id]);

  const fetchMeetings = async () => {
    try {
      const res = await api.get('/meetings');
      setMeetings(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const validEmail = (e) => /^[^\s@,;<>]+@[^\s@,;<>]+\.[a-z]{2,}$/i.test(e);
  const addEmail = (raw) => {
    const list = String(raw || '').split(/[,;\s]+/).map(x => x.trim().toLowerCase()).filter(validEmail);
    if (list.length) setForm(f => ({ ...f, emails: [...new Set([...f.emails, ...list])] }));
    setEmailInput('');
  };
  const closeForm = () => { setShowForm(false); setForm(EMPTY_FORM); setEmailInput(''); setFormError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (form.google_calendar && !form.meeting_date) { setFormError('Takvime eklemek için tarih ve saat seçin'); return; }
    const pending = emailInput.trim() ? emailInput.split(/[,;\s]+/).map(x => x.trim().toLowerCase()).filter(validEmail) : [];
    const emails = [...new Set([...form.emails, ...pending])];
    setSaving(true);
    try {
      const payload = {
        title: form.title, notes: form.notes,
        lead_id: form.lead_id || null,
        account_id: form.account_id || null,
        // tarayıcının saat diliminde seçilen saat → ISO (sunucu UTC)
        meeting_date: form.meeting_date ? new Date(form.meeting_date).toISOString() : null,
        duration_minutes: parseInt(form.duration_minutes) || null,
        attendees: form.google_calendar ? null : form.attendees,
        google_calendar: !!form.google_calendar,
        attendee_emails: emails,
      };
      const r = await api.post('/meetings', payload);
      closeForm();
      await fetchMeetings();
      if (r.data?.meet_link) toast.success('Toplantı Google Takvim\'e eklendi, Meet linki oluşturuldu');
      else toast.success('Toplantı kaydedildi');
    } catch (err) {
      setFormError(err.response?.data?.error || 'Toplantı kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const syncFireflies = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/meetings/fireflies/sync', {});
      const d = r.data || {};
      alert(d.skipped ? `Senkron yapılmadı: ${d.skipped}`
        : `Fireflies: ${d.fetched} kayıt okundu · ${d.created} yeni · ${d.updated} güncellendi · ${d.items_added} yeni madde`
          + (d.errors?.length ? ` · ${d.errors.length} hata` : ''));
      fetchMeetings();
    } catch (err) {
      alert(err.response?.data?.error || 'Senkron başarısız');
    } finally {
      setSyncing(false);
    }
  };

  const now = new Date();
  const q = search.trim().toLocaleLowerCase('tr');
  const match = (m) => !q || [m.title, m.lead_company, m.account_company, m.attendees, m.summary]
    .some(v => v && v.toLocaleLowerCase('tr').includes(q));
  const isPast = (m) => m.meeting_date && new Date(m.meeting_date) < now;
  const live = meetings.filter(m => m.calendar_status !== 'cancelled');
  const myEmail = String(user?.email || '').toLowerCase();
  const isMine = (m) => (myEmail && ((m.participant_emails || []).some(e => String(e).toLowerCase() === myEmail)
    || String(m.attendees || '').toLowerCase().includes(myEmail) || String(m.organizer_email || '').toLowerCase() === myEmail))
    || (m.created_by && m.created_by === user?.id);
  const lists = {
    upcoming: live.filter(m => !isPast(m)).sort((a, b) => new Date(a.meeting_date) - new Date(b.meeting_date)),
    recorded: live.filter(m => isPast(m) && m.fireflies_id),
    other: live.filter(m => isPast(m) && !m.fireflies_id),
  };
  useSubnavCounts({ upcoming: lists.upcoming.length, recorded: lists.recorded.length, other: lists.other.length });
  const shown = (lists[tab] || []).filter(match);

  // Güne göre grupla: Bugün / Dün / Yarın / 15 Ekim Perşembe
  const dayLabel = (d) => {
    const x = new Date(d); const t = new Date(); t.setHours(0, 0, 0, 0);
    const day = new Date(x); day.setHours(0, 0, 0, 0);
    const diff = Math.round((day - t) / 86400000);
    if (diff === 0) return 'Bugün';
    if (diff === -1) return 'Dün';
    if (diff === 1) return 'Yarın';
    return x.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', weekday: 'long', year: x.getFullYear() !== t.getFullYear() ? 'numeric' : undefined });
  };
  const groups = [];
  for (const m of shown) {
    const label = m.meeting_date ? dayLabel(m.meeting_date) : 'Tarihsiz';
    if (!groups.length || groups[groups.length - 1].label !== label) groups.push({ label, items: [] });
    groups[groups.length - 1].items.push(m);
  }

  const timeOf = (d) => (d ? new Date(d).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '—');

  const MeetingRow = ({ meeting: m }) => {
    const topics = m.summary ? parseSummary(m.summary).map(t => t.topic).filter(Boolean) : [];
    const people = Array.isArray(m.participant_emails) ? m.participant_emails.length : null;
    const brand = m.account_company || m.lead_company;
    const open = m.fireflies_id ? () => setPlayer({ meeting: m }) : undefined;
    return (
      <div className={`mrow${open ? ' mrow--link' : ''}`} onClick={open}>
        <div className="mrow__time">
          {timeOf(m.meeting_date)}
          {m.duration_minutes ? <span>{m.duration_minutes} dk</span> : null}
        </div>
        <div className="mrow__main">
          <div className="mrow__title">
            {m.title}
            {brand && <span className="mrow__brand">{brand}</span>}
          </div>
          {topics.length > 0 ? (
            <div className="mrow__topics">
              {topics.slice(0, 4).map(t => <span key={t}>{t}</span>)}
              {topics.length > 4 && <em>+{topics.length - 4}</em>}
            </div>
          ) : m.notes || m.outcome ? (
            <div className="mrow__note">{m.outcome || m.notes}</div>
          ) : null}
        </div>
        <div className="mrow__meta">
          {(people || m.attendees) && (
            <span title={m.attendees || ''}><Icon name="users" size={13} /> {people || String(m.attendees).split(',').filter(Boolean).length}</span>
          )}
          {m.item_count > 0 && (
            <span title="Yapılacaklar · bekleyen">
              <Icon name="clipboard" size={13} /> {m.item_count}{m.open_item_count ? <b> · {m.open_item_count} bekliyor</b> : ''}
            </span>
          )}
        </div>
        <div className="mrow__action">
          {joinState(m) && joinState(m) !== 'past' && (
            <a className={`btn ${joinState(m) === 'now' ? 'btn--primary' : 'btn--secondary'}`} href={joinUrl(m.meet_link, user?.email)} target="_blank" rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()} style={{ textDecoration: 'none' }}>
              <Icon name="video" size={12} /> Katıl
            </a>
          )}
          {m.fireflies_id && (
            <button type="button" className="btn btn--secondary" onClick={(e) => { e.stopPropagation(); setPlayer({ meeting: m }); }}>
              <Icon name="play" size={12} /> İzle
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{TAB_INFO[tab]?.title || 'Toplantılar'}</h1>
          <p className="page-subtitle">
            {tab === 'calendar' ? 'operations@boldranks.com takvimi — toplantıya tıklayınca Meet linki ve ayrıntılar açılır'
              : tab === 'recorded' ? "Fireflies kayıtları — satıra tıklayınca video, özet ve yapılacaklar açılır" : 'Planlanan ve geçmiş toplantılar'}
            {!can('meetings.view_all') && <> · Katıldığın, eklediğin veya sorumlusu olduğun markaların toplantıları görünür</>}
          </p>
        </div>
        <div className="page-actions">
          {tab === 'calendar' && (
            <div className="mcal__views" role="group" aria-label="Hangi toplantılar">
              {[['mine', 'Benim toplantılarım'], ['all', 'Tümü']].map(([k, t]) => (
                <button key={k} type="button" className={`mcal__view ${scope === k ? 'is-active' : ''}`} onClick={() => changeScope(k)}>{t}</button>
              ))}
            </div>
          )}
          <div className="search-bar">
            <span className="search-bar__icon"><Icon name="search" size={15} /></span>
            <input className="search-bar__input" placeholder="Toplantı / marka ara..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {can('meetings.sync') && (
            <button className="btn btn--secondary" onClick={syncFireflies} disabled={syncing} title="Fireflies'taki yeni toplantıları şimdi çek (otomatik olarak 30 dk'da bir de çekilir)">
              {syncing ? 'Çekiliyor…' : <><Icon name="refresh" size={14} /> Fireflies'tan Çek</>}
            </button>
          )}
          <button className="btn btn--primary" onClick={() => setShowForm(true)}>+ Toplantı Ekle</button>
        </div>
      </div>

      {loading ? (
        <div className="page-loading">Yükleniyor...</div>
      ) : (
        <>
          {tab === 'calendar' ? (
            <MeetingCalendar meetings={live.filter(match).filter(m => scope === 'all' || isMine(m))} users={users} onPlay={(m) => setPlayer({ meeting: m })} />
          ) : groups.length === 0 ? (
            <div className="card text-muted" style={{ textAlign: 'center', padding: 40 }}>
              {q ? 'Aramaya uyan toplantı yok' : TAB_INFO[tab]?.empty}
            </div>
          ) : groups.map(g => (
            <section key={g.label} className="mgroup">
              <h3 className="mgroup__day">{g.label}<span>{g.items.length}</span></h3>
              <div className="mgroup__list">
                {g.items.map(m => <MeetingRow key={m.id} meeting={m} />)}
              </div>
            </section>
          ))}

        </>
      )}

      {player && <MeetingVideoModal meeting={player.meeting} startAt={player.startAt} onClose={() => setPlayer(null)} onChanged={fetchMeetings} />}

      {/* Yeni toplantı: Google Takvim (operations@) + Meet linki + davet */}
      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal__title" style={{ marginBottom: 20 }}>Toplantı Ekle</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Başlık *</label>
                <input className="form-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required
                  placeholder="Boldranks - Marka - Meeting" />
              </div>
              <div className="form-group">
                <label className="form-label">Marka</label>
                <select className="form-input" value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })}>
                  <option value="">Markasız</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.brand_name || a.company_name}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Tarih ve Saat{form.google_calendar ? ' *' : ''}</label>
                  <input className="form-input" type="datetime-local" value={form.meeting_date} onChange={(e) => setForm({ ...form, meeting_date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Süre (dakika)</label>
                  <select className="form-input" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}>
                    {[15, 30, 45, 60, 90, 120].map(d => <option key={d} value={d}>{d} dk</option>)}
                  </select>
                </div>
              </div>
              <label className="mform-check">
                <input type="checkbox" checked={form.google_calendar} onChange={(e) => setForm({ ...form, google_calendar: e.target.checked })} />
                <span>Google Takvim'e ekle ve Meet linki oluştur
                  <small>operations@boldranks.com takvimine eklenir; davetlilere Google Takvim daveti e-postası gider.</small>
                </span>
              </label>
              {form.google_calendar ? (
                <div className="form-group">
                  <label className="form-label">Davetliler</label>
                  <div className="mform-people">
                    {form.emails.map(e => (
                      <span key={e} className="mform-chip">{(users.find(u => (u.email || '').toLowerCase() === e)?.name) || e}
                        <button type="button" aria-label="Çıkar" onClick={() => setForm(f => ({ ...f, emails: f.emails.filter(x => x !== e) }))}><Icon name="x" size={10} /></button>
                      </span>
                    ))}
                    {form.emails.length === 0 && <span className="text-muted" style={{ fontSize: 12 }}>Sen otomatik davetlisin. Başka kimse eklenmedi.</span>}
                  </div>
                  <input className="form-input" style={{ marginTop: 8 }} value={emailInput} placeholder="E-posta yazıp Enter (virgülle birden fazla)"
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addEmail(emailInput); } }}
                    onBlur={() => emailInput && addEmail(emailInput)} />
                  {contacts.filter(c => !form.emails.includes(c.email.toLowerCase())).length > 0 && (
                    <div className="mform-pick">
                      <span className="text-muted" style={{ fontSize: 12, alignSelf: 'center' }}>Marka kontakları:</span>
                      {contacts.filter(c => !form.emails.includes(c.email.toLowerCase())).map(c => (
                        <button type="button" key={c.id} onClick={() => addEmail(c.email)}>+ {c.name || c.email}</button>
                      ))}
                    </div>
                  )}
                  <div className="mform-pick">
                    <span className="text-muted" style={{ fontSize: 12, alignSelf: 'center' }}>Ekip:</span>
                    {users.filter(u => u.is_active !== false && u.email && u.email.toLowerCase() !== (user?.email || '').toLowerCase() && !form.emails.includes(u.email.toLowerCase())).map(u => (
                      <button type="button" key={u.id} onClick={() => addEmail(u.email)}>+ {u.name}</button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="form-group">
                  <label className="form-label">Katılımcılar</label>
                  <input className="form-input" value={form.attendees} onChange={(e) => setForm({ ...form, attendees: e.target.value })} placeholder="Ahmet, John Doe..." />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">{form.google_calendar ? 'Açıklama (davette görünür)' : 'Notlar'}</label>
                <textarea className="form-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} style={{ resize: 'vertical' }} />
              </div>
              {formError && <div className="sc-note" style={{ marginBottom: 10 }}>{formError}</div>}
              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <button type="submit" className="btn btn--primary" style={{ flex: 1 }} disabled={saving}>
                  {saving ? 'Kaydediliyor…' : form.google_calendar ? 'Takvime ekle ve davet gönder' : 'Kaydet'}
                </button>
                <button type="button" className="btn btn--secondary" style={{ flex: 1 }} onClick={closeForm}>İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
